import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { TSourceDetail } from '@core/interfaces/TMessage';

import { StringHelper } from '../../../../helpers/string.helper';
import { TConversationType } from '../../../types/types';

import { SourceViewerData, SourceViewerSidebarComponent } from './source-viewer-sidebar.component';

interface SourceDisplay {
  preview: string;
  fullPreview: string;
}

@Component({
    selector: 'app-original-content',
    templateUrl: './original-content.component.html',
    styleUrls: ['./original-content.component.scss'],
    animations: [
        trigger('expand', [
            transition(':enter', [
                style({ maxHeight: 0, opacity: 0 }),
                animate('2s ease', style({ maxHeight: 'auto', opacity: 1 })),
            ], { params: { skipAnimation: false } }),
            transition(':leave', [animate('2s ease', style({ maxHeight: 0, opacity: 0 }))], {
                params: { skipAnimation: false },
            }),
        ]),
    ],
    standalone: true,
    imports: [MatIcon, SourceViewerSidebarComponent]
})
export class OriginalContentComponent implements OnInit {
  @Input() content: string[] | string | undefined = undefined;
  @Input() sources: TSourceDetail[] = [];
  @Input() conversationType: TConversationType = 'conversation';
  @Input() conversationData: any = null; // Add conversation data input for searchedSources and processedSegments
  @Input() conversationId?: string; // For lazy loading source content in results view
  displaySources: SourceDisplay[] = [];
  moreSources: SourceDisplay[] = [];
  hasMoreSources = false;

  // Source viewer sidebar properties
  sidebarVisible = false;
  sidebarData: SourceViewerData = { sources: [] };
  activeSourceIndex = 0;

  ngOnInit(): void {
    // Always process sources (from searchedSources), ignore content input
    this.processSources();
  }

  // Open source viewer sidebar
  openSourceViewer(sourceIndex: number): void {
    if (!this.sources || sourceIndex < 0 || sourceIndex >= this.sources.length) return;

    // If we have conversation data with searchedSources, use the new approach
    if (this.conversationData?.messages) {
      this.openSourceViewerFromConversationData(sourceIndex);
      return;
    }

    // Fallback to the old approach if conversation data is not available
    this.openSourceViewerFallback(sourceIndex);
  }

  private openSourceViewerFromConversationData(sourceIndex: number): void {
    // Work with ADAPTED conversation data (Conversation model, not raw DTO)
    // Extract referenced embedding IDs from message.paragraphs[].references[]
    const referencedEmbeddingIdsBySource = new Map<string, string[]>();
    
    // Find assistant messages with references in paragraphs
    const messages = this.conversationData.messages || [];
    messages.forEach((msg: any) => {
      if (msg.author === 'assistant' && msg.paragraphs) {
        msg.paragraphs.forEach((paragraph: any) => {
          paragraph.references?.forEach((ref: any) => {
            const sourceId = ref.sourceId;
            const embeddingId = ref.referenceDto?.embeddingId;
            if (sourceId && embeddingId) {
              const existing = referencedEmbeddingIdsBySource.get(sourceId) || [];
              if (!existing.includes(embeddingId)) {
                existing.push(embeddingId);
              }
              referencedEmbeddingIdsBySource.set(sourceId, existing);
            }
          });
        });
      }
    });

    // ROLLBACK (source viewer sidebar): To show only cited sources again (e.g. 13 instead of 34), add back
    // after the forEach above: if (referencedEmbeddingIdsBySource.size === 0) {
    //   this.openSourceViewerFallback(sourceIndex); return;
    // }
    // and inside the uniqueSources.forEach below add: if (referencedIds.length === 0) return;
    // Get unique sources (same set as grid)
    const uniqueSources = new Map<string, TSourceDetail>();
    this.sources.forEach(source => {
      if (!uniqueSources.has(source._id)) {
        uniqueSources.set(source._id, source);
      }
    });

    // Build mapped sources - content will be lazy loaded from new endpoint
    const mappedSources: any[] = [];
    // Include ALL grid sources in sidebar so clicking any tile opens the correct source
    uniqueSources.forEach((source, sourceId) => {
      const referencedIds = referencedEmbeddingIdsBySource.get(sourceId) || [];

      // Check if we already have content (from non-metadata-only response)
      const hasFullContent = source.references && source.references.length > 50;
      const hasEmbeddingContent = source.embeddingContent && source.embeddingContent.length > 0;

      mappedSources.push({
        index: mappedSources.length + 1,
        _id: source._id,
        name: source.name || 'Untitled Source',
        preview: this.truncateText(source.name || 'Untitled Source'),
        // Content will be lazy loaded if not available
        fullPreview: hasFullContent ? (source.references || '') : '',
        folder: source.folder,
        fileLink: source.fileLink,
        embeddingContent: hasEmbeddingContent ? source.embeddingContent : undefined,
        embeddingID: undefined,
        uiExternalRefsToDisplay: source.uiExternalRefsToDisplay,
        // Chunks will be populated by lazy load - filtered by referencedEmbeddingIds
        chunks: [],
        // Store referenced embedding IDs for filtering after lazy load
        referencedEmbeddingIds: referencedIds,
        isLoaded: false, // Force lazy load to get fresh data with content
      });
    });

    if (mappedSources.length === 0) {
      this.openSourceViewerFallback(sourceIndex);
      return;
    }

    // Find the correct active index
    const clickedSource = this.sources[sourceIndex];
    const activeIndex = mappedSources.findIndex(source => source._id === clickedSource._id);

    this.sidebarData = {
      sources: mappedSources,
      activeIndex: activeIndex >= 0 ? activeIndex : 0,
    };

    this.activeSourceIndex = activeIndex >= 0 ? activeIndex : 0;
    this.sidebarVisible = true;
  }

  private openSourceViewerFallback(sourceIndex: number): void {
    // Get the original TSourceDetail objects (deduplicated)
    const uniqueSources = new Map<string, TSourceDetail>();
    this.sources.forEach(source => {
      if (!uniqueSources.has(source._id)) {
        uniqueSources.set(source._id, source);
      }
    });
    const originalSources = Array.from(uniqueSources.values());

    // Group ALL sources by _id and collect all embeddings for each source
    const sourceGroups = new Map<
      string,
      {
        _id: string;
        name: string;
        folder?: string[];
        fileLink?: string;
        references?: string;
        uiExternalRefsToDisplay?: string[];
        embeddingContents: string[];
        embeddingIds: string[];
      }
    >();

    this.sources.forEach(source => {
      if (!sourceGroups.has(source._id)) {
        sourceGroups.set(source._id, {
          _id: source._id,
          name: source.name,
          folder: source.folder,
          fileLink: source.fileLink,
          references: source.references,
          uiExternalRefsToDisplay: source.uiExternalRefsToDisplay,
          embeddingContents: [],
          embeddingIds: [],
        });
      }

      const group = sourceGroups.get(source._id)!;

      // Add embedding content and ID if they exist and aren't already added
      if (source.embeddingContent && !group.embeddingContents.includes(source.embeddingContent)) {
        group.embeddingContents.push(source.embeddingContent);
      }
      if (source.embeddingID && !group.embeddingIds.includes(source.embeddingID)) {
        group.embeddingIds.push(source.embeddingID);
      }
    });

    const groupedSources = Array.from(sourceGroups.values());

    // Check if we need lazy loading (results view with conversationId)
    const needsLazyLoading = this.conversationType === 'results' && !!this.conversationId;

    const mappedSources = groupedSources.map((source, index) => {
      const hasContent = !!(source.references && source.references.length > 50);
      const hasEmbeddingContent = source.embeddingContents.length > 0;
      
      return {
        index: index + 1,
        _id: source._id,
        name: source.name || 'Untitled Source',
        preview: this.truncateText(source.name || 'Untitled Source'),
        // If no content and needs lazy loading, leave empty for lazy load
        fullPreview: hasContent ? (source.references || '') : (needsLazyLoading ? '' : 'No content available'),
        folder: source.folder,
        fileLink: source.fileLink,
        embeddingContent: source.embeddingContents[0],
        embeddingID: undefined,
        uiExternalRefsToDisplay: source.uiExternalRefsToDisplay,
        // Chunks: use existing if available, otherwise lazy load will populate
        chunks: hasEmbeddingContent ? source.embeddingContents : [],
        // Store embedding IDs for filtering (may be used if lazy loading)
        referencedEmbeddingIds: source.embeddingIds,
        // Force lazy load if in results view without content
        isLoaded: !needsLazyLoading || (hasContent && hasEmbeddingContent),
      };
    });

    // Find the correct active index in the grouped sources
    const clickedSource = originalSources[sourceIndex];
    const activeIndex = mappedSources.findIndex(source => source._id === clickedSource._id);

    this.sidebarData = {
      sources: mappedSources,
      activeIndex: activeIndex >= 0 ? activeIndex : 0,
    };

    this.activeSourceIndex = activeIndex >= 0 ? activeIndex : 0;
    this.sidebarVisible = true;
  }

  // Close the sidebar
  closeSidebar(): void {
    this.sidebarVisible = false;
  }

  // Truncate text to approximately 10 words
  private truncateText(text: string): string {
    return StringHelper.truncateToWords(text);
  }

  private processSources(): void {
    // Deduplicate sources by _id to avoid showing duplicate tiles for same source with multiple embeddings
    const uniqueSources = new Map<string, TSourceDetail>();

    this.sources.forEach(source => {
      if (!uniqueSources.has(source._id)) {
        uniqueSources.set(source._id, source);
      }
    });

    // Process deduplicated sources
    Array.from(uniqueSources.values()).forEach((source, index) => {
      // Use source name for preview (tile title)
      const previewText = source.name || 'Untitled Source';
      // Use full content for sidebar
      const fullText = source.references || 'No full content available';

      const sourceDisplay: SourceDisplay = {
        preview: this.truncateText(previewText),
        fullPreview: fullText,
      };

      if (index < 3) {
        this.displaySources.push(sourceDisplay);
      } else {
        this.moreSources.push(sourceDisplay);
      }
    });

    this.hasMoreSources = this.moreSources.length > 0;
  }
}
